import { logInfo } from './logger';

/**
 * AbortController pool that dynamically grows as needed.
 * - Minimum number of controllers is 5
 * - When exceeding 50 controllers, a warning about the increase is logged
 * - Ensures reuse of controllers
 */
class AbortControllerPool {
	private static instance: AbortControllerPool;
	private freeControllers: AbortController[] = [];
	private activeControllers: Map<string, AbortController> = new Map();
	private readonly MIN_SIZE = 5;
	private readonly WARNING_THRESHOLD = 50;

	private constructor() {
		// Initialize the minimum number of AbortControllers
		this.ensureMinPoolSize();
	}

	static getInstance(): AbortControllerPool {
		if (!AbortControllerPool.instance) {
			AbortControllerPool.instance = new AbortControllerPool();
		}
		return AbortControllerPool.instance;
	}

	/**
	 * Ensures that the pool always has a minimum number of free controllers.
	 */
	private ensureMinPoolSize(): void {
		while (this.freeControllers.length < this.MIN_SIZE) {
			this.freeControllers.push(new AbortController());
		}
	}

	/**
	 * Gets an AbortController from the pool. If no unused controller is available,
	 * creates a new one and expands the pool.
	 */
	getController(requestId: string): AbortController {
		// If we already have a controller for this request, return it
		if (this.activeControllers.has(requestId)) {
			return this.activeControllers.get(requestId)!;
		}

		// If we don't have any free controllers, create a new one
		if (this.getFreeCount() === 0) {
			this.freeControllers.push(new AbortController());
		}

		// Take a controller from the free ones
		const controller = this.freeControllers.pop()!;
		this.activeControllers.set(requestId, controller);

		// Log a warning when exceeding the threshold
		const totalSize = this.getTotalSize();
		if (totalSize > this.WARNING_THRESHOLD && totalSize % 10 === 0) {
			logInfo(`AbortControllerPool: Pool size exceeded ${this.WARNING_THRESHOLD} (currently ${totalSize})`);
		}

		return controller;
	}

	/**
	 * Cancels the request and releases the controller
	 */
	abort(requestId: string): void {
		const controller = this.activeControllers.get(requestId);
		if (controller) {
			controller.abort();
			this.releaseController(requestId);
		}
	}

	/**
	 * Releases an AbortController back to the pool for further use.
	 */
	releaseController(requestId: string): void {
		const controller = this.activeControllers.get(requestId);
		if (controller) {
			this.activeControllers.delete(requestId);

			// AbortController cannot be reset, so we create a new one
			// and add it to the free ones
			this.freeControllers.push(new AbortController());

			// Ensure minimum pool size
			this.ensureMinPoolSize();
		}
	}

	/**
	 * Returns the total size of the pool (active + free controllers).
	 */
	getTotalSize(): number {
		return this.getActiveCount() + this.getFreeCount();
	}

	/**
	 * Returns the number of currently used controllers.
	 */
	getActiveCount(): number {
		return this.activeControllers.size;
	}

	/**
	 * Returns the number of available (unused) controllers.
	 */
	getFreeCount(): number {
		return this.freeControllers.length;
	}
}

// Export the pool instance for global use
export const abortControllerPool = AbortControllerPool.getInstance();
